import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { TrendingUp, Users, Package, RefreshCw, Settings2, Lock } from 'lucide-react';
import { FilterPanel } from '@/components/FilterPanel';
import { HelpBubble } from '@/components/HelpBubble';
import { ExportDropdown } from '@/components/ExportDropdown';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { downloadCSV, buildPDF } from '@/lib/exportUtils';
import CategoryManagement from '@/components/CategoryManagement';
import { useUser } from '@/lib/UserContext';
import api from '@/lib/api';
import { AIPanel } from '@/components/AIPanel';
import { toast } from 'sonner';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';
import { StrategySection } from '@/components/categoryConsole/StrategySection';
import { HierarchyTab } from '@/components/categoryConsole/HierarchyTab';
import { OverviewTab } from '@/components/categoryConsole/OverviewTab';
import { SegmentationTab } from '@/components/categoryConsole/SegmentationTab';
import { TrendsTab } from '@/components/categoryConsole/TrendsTab';

export default function CategoryConsole() {
  const { symbol: cs } = useCurrency();
  const [data, setData] = useState(null);
  const [hierarchy, setHierarchy] = useState(null);
  const currentUser = useUser();
  const isSuperAdminUser = (currentUser?.roles || []).includes('super_admin');
  const hasCategoryManagement = isSuperAdminUser || (currentUser?.subscribed_features || []).includes('category_management');

  // Get user info for role checks
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRoles = user?.roles || [];
  const userCaps = user?.ai_capabilities || currentUser?.ai_capabilities || {};
  const hasStrategyCap = isSuperAdminUser || userCaps.ai_category_strategy !== false;
  const roleAllowsStrategy = userRoles.includes('super_admin') || userRoles.includes('category_manager') || userRoles.includes('executive');
  const canGenerateStrategy = roleAllowsStrategy && hasCategoryManagement && hasStrategyCap && (currentUser?.ai_access !== false);
  const [segmentation, setSegmentation] = useState(null);
  const [categoryLevelSegmentation, setCategoryLevelSegmentation] = useState(null);
  const [categoryTrends, setCategoryTrends] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('level_1');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [strategyResetKey, setStrategyResetKey] = useState(0);

  const { filters, setFilters } = useGlobalFilters();

  const buildQueryString = useCallback((filterObj) => {
    const params = new URLSearchParams();
    if (filterObj.category) params.append('category', filterObj.category);
    if (filterObj.category_l2) params.append('category_l2', filterObj.category_l2);
    if (filterObj.category_l3) params.append('category_l3', filterObj.category_l3);
    if (filterObj.supplier) params.append('supplier', filterObj.supplier);
    if (filterObj.region) params.append('region', filterObj.region);
    if (filterObj.country) params.append('country', filterObj.country);
    if (filterObj.sbu) params.append('sbu', filterObj.sbu);
    if (filterObj.cost_center) params.append('cost_center', filterObj.cost_center);
    if (filterObj.business_unit) params.append('business_unit', filterObj.business_unit);
    if (filterObj.addressability) params.append('addressability', filterObj.addressability);
    if (filterObj.fiscal_year) params.append('fiscal_year', filterObj.fiscal_year);
    if (filterObj.startDate) params.append('start_date', filterObj.startDate);
    if (filterObj.endDate) params.append('end_date', filterObj.endDate);
    return params.toString();
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (filterObj = filters) => {
    try {
      const queryString = buildQueryString(filterObj);
      const [consoleRes, hierarchyRes, segmentRes, categoryLevelRes, trendsRes] = await Promise.all([
        api.get(`/analytics/category-console${queryString ? `?${queryString}` : ''}`),
        api.get(`/analytics/category-hierarchy${queryString ? `?${queryString}` : ''}`),
        api.get(`/analytics/spend-segmentation${queryString ? `?${queryString}` : ''}`),
        api.get(`/analytics/category-level-segmentation${queryString ? `?${queryString}` : ''}`),
        api.get(`/analytics/category-level1-trends${queryString ? `?${queryString}` : ''}`),
      ]);
      setData(consoleRes.data);
      setHierarchy(hierarchyRes.data);
      setSegmentation(segmentRes.data);
      setCategoryLevelSegmentation(categoryLevelRes.data);
      setCategoryTrends(trendsRes.data);
      if (consoleRes.data.categories.length > 0) {
        setSelectedCategory(consoleRes.data.categories[0]);
      }
    } catch (error) {
      toast.error('Failed to load category data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setRefreshing(true);
    fetchData(newFilters);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setStrategyResetKey(k => k + 1);
    fetchData(filters);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const activeFiltersCount = Object.values(filters).filter(v => v !== null && v !== '' && v !== 'all').length;

  const exportCSV = () => {
    const rows = [
      ['--- Summary ---'],
      ['Total Categories', data?.total_categories || 0],
      ['Total Spend', `${cs}${data?.total_spend?.toLocaleString() || 0}`],
      ['Top Category', data?.categories?.[0]?.category || 'N/A'],
      [],
      ['--- Categories ---'],
      ['Category', 'Spend', '% Total', 'Transactions', 'Suppliers'],
      ...(data?.categories || []).map(c => [c.category, c.total_spend, `${c.spend_percentage?.toFixed(1)}%`, c.transaction_count, c.supplier_count]),
    ];
    if (categoryLevelSegmentation?.[selectedLevel]?.length > 0) {
      rows.push([], [`--- Segmentation (${selectedLevel}) ---`], ['Category', 'Parent', 'Spend', '% Total', 'Transactions', 'Suppliers']);
      categoryLevelSegmentation[selectedLevel].forEach(c => rows.push([c.category, c.parent || '', c.spend, `${c.spend_percentage?.toFixed(1)}%`, c.transaction_count, c.supplier_count]));
    }
    downloadCSV(rows, 'category_console');
  };

  const exportSourceData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.category_l2) params.append('category_l2', filters.category_l2);
      if (filters.category_l3) params.append('category_l3', filters.category_l3);
      if (filters.supplier) params.append('supplier', filters.supplier);
      if (filters.region) params.append('region', filters.region);
      if (filters.country) params.append('country', filters.country);
      if (filters.sbu) params.append('entity', filters.sbu);
      if (filters.fiscal_year) params.append('fiscal_year', filters.fiscal_year);
      params.append('limit', '50000');
      const res = await api.get(`/transactions/filtered?${params}`);
      const txns = res.data.transactions || [];
      if (txns.length === 0) { toast.info('No transactions match the current filters'); return; }

      const SOURCE_FIELDS = [
        'invoice_date', 'supplier_name', 'description', 'invoice_number',
        'spend_amount', 'currency', 'category_level_1', 'category_level_2',
        'category_level_3', 'category_level_4', 'region', 'country', 'sbu',
        'payment_terms', 'po_number', 'contracted_spend', 'unit_price',
        'order_quantity', 'payment_date', 'fiscal_year', 'buyer_name',
        'office_location', 'gl_code', 'gl_description',
      ];
      const headers = SOURCE_FIELDS.map(f => f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
      const rows = [headers];
      txns.forEach(t => {
        rows.push(SOURCE_FIELDS.map(f => {
          const v = t[f] ?? t[f === 'description' ? 'invoice_line_description' : f] ?? '';
          return v;
        }));
      });

      const filterLabel = [filters.supplier, filters.category, filters.fiscal_year].filter(Boolean).join('_') || 'all';
      downloadCSV(rows, `source_data_${filterLabel}`);
      toast.success(`Exported ${txns.length} source transactions`);
    } catch {
      toast.error('Failed to export source data');
    }
  };

  const exportPDF = () => {
    const sections = [
      { title: 'Summary', headers: ['Metric', 'Value'], rows: [['Total Categories', String(data?.total_categories || 0)], ['Total Spend', `${cs}${data?.total_spend?.toLocaleString() || 0}`], ['Top Category', data?.categories?.[0]?.category || 'N/A']] },
      { title: 'Categories', headers: ['Category', 'Spend', '% Total', 'Transactions', 'Suppliers'], rows: (data?.categories || []).map(c => [c.category, `${cs}${c.total_spend?.toLocaleString()}`, `${c.spend_percentage?.toFixed(1)}%`, String(c.transaction_count), String(c.supplier_count)]) },
    ];
    if (categoryLevelSegmentation?.[selectedLevel]?.length > 0) {
      sections.push({ title: `Segmentation (${selectedLevel})`, headers: ['Category', 'Parent', 'Spend', '% Total'], rows: categoryLevelSegmentation[selectedLevel].map(c => [c.category, c.parent || '', `${cs}${c.spend?.toLocaleString()}`, `${c.spend_percentage?.toFixed(1)}%`]) });
    }
    buildPDF(sections, 'Category_Console');
  };

  return (
    <div className="space-y-4" data-testid="category-console-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Category Console</h1>
          <p className="text-muted-foreground mt-1">
            Hierarchical category analysis and value segmentation
            {activeFiltersCount > 0 && (
              <span className="ml-2 text-primary font-medium">
                ({activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} applied)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TrustScoreBadge pageName="category-console" />
          <ExportDropdown onExportCSV={exportCSV} onExportPDF={exportPDF} onExportSourceData={exportSourceData} />
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh} 
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      <FilterPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        pageName="category-console"
        visibleFilters={['category', 'category_l2', 'category_l3', 'supplier', 'region', 'country', 'sbu', 'fiscal_year', 'cost_center', 'business_unit']}
        showDateRange={true}
        compact={true}
      />

      {/* AI Strategy Card */}
      {/* Selected Category Deep Dive Banner */}
      {filters.category && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }} 
          animate={{ opacity: 1, y: 0 }}
          className="bg-primary/10 border border-primary/30 rounded-lg p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-primary">Deep Dive: {filters.category}</h3>
              <p className="text-sm text-muted-foreground">Viewing detailed analysis for this category</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleFiltersChange({ ...filters, category: null })}
            >
              Clear Category Filter
            </Button>
          </div>
        </motion.div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">TOTAL CATEGORIES <HelpBubble text="Distinct L1 categories with spend. A very low number can mean under-classification — try enriching with L2/L3 columns for deeper analysis." /></CardTitle>
            <Package className="w-5 h-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono">{data?.total_categories || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">TOTAL SPEND <HelpBubble text="Sum of spend across all shown categories, FX-normalised. Use the tree / treemap below to see the L1 → L2 → L3 breakdown." /></CardTitle>
            <TrendingUp className="w-5 h-5 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono">{cs}{data?.total_spend?.toLocaleString() || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">TOP CATEGORY <HelpBubble text="Highest-spend L1 category. If one category dominates, it's your priority for strategic sourcing effort — head to Spend Analysis for supplier concentration inside it." /></CardTitle>
            <Users className="w-5 h-5 text-chart-4" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">{data?.categories?.[0]?.category || 'N/A'}</div>
            <p className="text-sm text-muted-foreground">{cs}{data?.categories?.[0]?.total_spend?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="hierarchy">Hierarchy</TabsTrigger>
          <TabsTrigger value="segmentation">Segmentation</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="management" data-testid="management-tab"><Settings2 className="w-3.5 h-3.5 mr-1.5" />Management</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <OverviewTab data={data} cs={cs} />

        {/* Hierarchy Tab */}
        <HierarchyTab
          hierarchy={hierarchy}
          selectedLevel={selectedLevel}
          setSelectedLevel={setSelectedLevel}
          cs={cs}
        />

        {/* Segmentation Tab */}
        <SegmentationTab
          categoryLevelSegmentation={categoryLevelSegmentation}
          selectedLevel={selectedLevel}
          setSelectedLevel={setSelectedLevel}
          cs={cs}
        />

        {/* Trends Tab */}
        <TrendsTab categoryTrends={categoryTrends} cs={cs} />

        {/* Management Tab */}
        <TabsContent value="management">
          {hasCategoryManagement ? (
            <CategoryManagement />
          ) : (
            <Card data-testid="category-management-locked">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Lock className="w-6 h-6 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-1">Category Management</h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  AI auto-classification, taxonomy management, and bulk reclassification require a premium subscription. Contact your administrator to enable this feature.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* AI Category Strategy — Management tab only; force-mounted so a generated strategy survives tab switches */}
        {hasCategoryManagement && (
          <TabsContent value="management" forceMount className="data-[state=inactive]:hidden space-y-4" data-testid="management-strategy-section">
            <StrategySection
              canGenerateStrategy={canGenerateStrategy}
              filters={filters}
              cs={cs}
              resetKey={strategyResetKey}
            />
          </TabsContent>
        )}
      </Tabs>

      <AIPanel page="category-console" filters={filters} />
    </div>
  );
}